import { useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useEffect } from "react";
import api, { apiError, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Article } from "@/lib/types";
import { ArticleCard } from "@/components/ArticleCard";
import { ReferralPanel } from "@/components/ReferralPanel";
import { WatchlistPanel } from "@/components/WatchlistPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function Account() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  const { data: saved } = useQuery({
    queryKey: ["saved"],
    queryFn: async () => (await api.get("/account/saved")).data,
    enabled: !!user,
  });

  const { data: history } = useQuery({
    queryKey: ["reading-history"],
    queryFn: async () => (await api.get("/account/history")).data,
    enabled: !!user,
  });

  const { data: storyFollows } = useQuery({
    queryKey: ["story-follows"],
    queryFn: async () => (await api.get("/story-alerts/follows")).data,
    enabled: !!user,
  });

  if (loading || !user) return <div className="py-24 text-center text-gray-500">Loading account...</div>;

  const following = [
    ...(user.followed_categories || []).map((s) => ({ type: "category", slug: s })),
    ...(user.followed_tags || []).map((s) => ({ type: "tag", slug: s })),
    ...(user.followed_entities || []).map((s) => ({ type: "entity", slug: s })),
  ];

  const unfollow = async (type: string, slug: string) => {
    try {
      await api.delete("/account/follow", { data: { follow_type: type, slug } });
      await refresh();
      toast.success(`Unfollowed ${slug}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const unfollowStory = async (slug: string) => {
    try {
      await api.delete(`/story-alerts/follow/${slug}`);
      queryClient.invalidateQueries({ queryKey: ["story-follows"] });
      toast.success("Story unfollowed");
    } catch (e) { toast.error(apiError(e)); }
  };

  const updatePref = async (group: string, key: string, value: boolean) => {
    try {
      const prefs = { ...(group === "newsletter_preferences" ? user.newsletter_preferences : user.notification_preferences), [key]: value };
      await api.patch("/account/preferences", { [group]: prefs });
      await refresh();
      toast.success("Preferences saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="account-page">
      <h1 className="font-heading font-black text-white text-3xl mb-1">My Account</h1>
      <p className="text-gray-500 mb-8 font-mono text-sm">{user.email} · <span className="uppercase">{user.role}</span></p>

      <Tabs defaultValue="saved">
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-none">
          <TabsList className="bg-[#121212] border border-white/10 w-max">
            <TabsTrigger value="saved" data-testid="account-tab-saved">Saved</TabsTrigger>
            <TabsTrigger value="history" data-testid="account-tab-history">History</TabsTrigger>
            <TabsTrigger value="following" data-testid="account-tab-following">Following</TabsTrigger>
            <TabsTrigger value="watchlist" data-testid="account-tab-watchlist">Watchlist</TabsTrigger>
            <TabsTrigger value="referrals" data-testid="account-tab-referrals">Invite & Earn</TabsTrigger>
            <TabsTrigger value="preferences" data-testid="account-tab-preferences">Preferences</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="watchlist" className="mt-6">
          <WatchlistPanel />
        </TabsContent>

        <TabsContent value="saved" className="mt-6">
          {(saved || []).length === 0 ? (
            <p className="text-gray-500 py-10">No saved stories yet. Hit the bookmark on any article.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(saved || []).map((a: Article) => <ArticleCard key={a.id} article={a} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          {(history || []).length === 0 ? (
            <p className="text-gray-500 py-10" data-testid="history-empty">
              No reading history yet. Every story you open while signed in shows up here.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="history-grid">
              {(history || []).map((a: Article & { read_at?: string }) => (
                <div key={a.id} className="flex flex-col gap-2">
                  <ArticleCard article={a} />
                  <p className="font-mono text-[10px] uppercase tracking-wide text-gray-500 px-1">
                    Read {timeAgo(a.read_at || "")}
                  </p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="following" className="mt-6">
          {(storyFollows || []).length > 0 && (
            <div className="mb-8" data-testid="story-alerts-panel">
              <h3 className="font-heading font-bold text-white text-sm uppercase tracking-wide mb-3">Story Alerts</h3>
              <div className="space-y-2 max-w-2xl">
                {(storyFollows || []).map((f: any) => (
                  <div key={f.anchor_slug} data-testid={`story-follow-${f.anchor_slug}`}
                    className="flex items-center justify-between gap-3 bg-[#121212] border border-white/10 rounded-lg px-4 py-3">
                    <div className="min-w-0">
                      <Link to={`/article/${f.anchor_slug}`}
                        className="text-white text-sm font-medium hover:text-[#00E5FF] transition-colors line-clamp-1">
                        {f.anchor_title}
                      </Link>
                      <p className="font-mono text-[10px] text-gray-500 mt-0.5">
                        Following since {timeAgo(f.created_at)}
                        {f.last_notified_at ? ` · last alert ${timeAgo(f.last_notified_at)}` : " · no alerts yet"}
                      </p>
                    </div>
                    <button onClick={() => unfollowStory(f.anchor_slug)}
                      data-testid={`unfollow-story-${f.anchor_slug}`}
                      className="text-gray-500 hover:text-red-400 font-mono text-xs shrink-0 transition-colors">
                      Unfollow
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {following.length === 0 && (storyFollows || []).length === 0 ? (
            <p className="text-gray-500 py-10">You're not following any topics or stories yet. Hit "Follow" on any article to get alerts as the story develops.</p>
          ) : following.length > 0 ? (
            <div className="flex flex-wrap gap-3">
              {following.map((f) => (
                <Badge key={`${f.type}-${f.slug}`} variant="outline"
                  className="border-white/20 text-white px-3 py-1.5 gap-2">
                  <span className="font-mono text-[10px] text-gray-500 uppercase">{f.type}</span> {f.slug}
                  <button onClick={() => unfollow(f.type, f.slug)} data-testid={`unfollow-${f.slug}`}
                    className="text-gray-500 hover:text-red-400 ml-1">×</button>
                </Badge>
              ))}
            </div>
          ) : null}
        </TabsContent>

        <TabsContent value="referrals" className="mt-6">
          <ReferralPanel />
        </TabsContent>

        <TabsContent value="preferences" className="mt-6 space-y-5 max-w-lg">
          <div className="flex items-center justify-between bg-[#121212] border border-white/10 rounded-lg p-5">
            <div>
              <p className="text-white font-medium">Daily digest newsletter</p>
              <p className="text-gray-500 text-sm">The day's top stories, every morning</p>
            </div>
            <Switch checked={!!user.newsletter_preferences?.daily_digest} data-testid="pref-daily-digest"
              onCheckedChange={(v) => updatePref("newsletter_preferences", "daily_digest", v)} />
          </div>
          <div className="flex items-center justify-between bg-[#121212] border border-white/10 rounded-lg p-5">
            <div>
              <p className="text-white font-medium">Breaking news alerts</p>
              <p className="text-gray-500 text-sm">Major market and regulation events</p>
            </div>
            <Switch checked={!!user.notification_preferences?.breaking} data-testid="pref-breaking"
              onCheckedChange={(v) => updatePref("notification_preferences", "breaking", v)} />
          </div>
          <Button variant="outline" data-testid="account-change-password-link"
            className="border-white/20 bg-transparent text-white hover:bg-white/5"
            onClick={() => navigate("/change-password")}>
            Change password
          </Button>
        </TabsContent>
      </Tabs>
    </div>
  );
}
